import { mollieUrl, paypalUrl } from '@/config';
import { BaseApi } from './baseApi';

/**
 * Payment method interface
 */
export interface IPaymentMethod {
  description: string;
  id: string;
  image: string;
  status: string;
}

/**
 * Payment provider properties
 */
export interface IPaymentProviderProps {
  mollie: boolean;
  paypal: boolean;
}

/**
 * Provider for handling payment methods and URL generation
 */
export class PaymentProvider extends BaseApi {
  private readonly _providers: IPaymentProviderProps;
  private readonly _quoteId: string;

  /**
   * Constructor
   * @param {string} quoteId - The ID of the quote
   * @param {IPaymentProviderProps} providers - Active payment providers
   */
  public constructor(quoteId: string, providers: IPaymentProviderProps) {
    super();
    this._quoteId = quoteId;
    this._providers = providers;
  }

  /**
   * Fetches payment methods from Mollie and includes PayPal if enabled
   * @param {string} locale - The current locale (e.g. 'it_IT')
   * @returns {Promise<IPaymentMethod[]>}
   */
  public async listMethods(locale: string): Promise<IPaymentMethod[]> {
    let methods: IPaymentMethod[] = [];

    if (this._providers.mollie && mollieUrl) {
      try {
        const response = await fetch(
          `${mollieUrl}/order/${this._quoteId}/methods?locale=${locale}`
        );

        if (response.ok) {
          const mollieMethods = (await response.json()) as IPaymentMethod[];
          methods.push(...mollieMethods);
        }
      } catch (error) {
        console.error('Error fetching Mollie payment methods:', error);
      }
    }

    if (this._providers.paypal) {
      methods = methods.filter((m) => m.id !== 'paypal');

      methods.push({
        description: 'PayPal',
        id: 'paypal-internal',
        image: 'https://www.mollie.com/external/icons/payment-methods/paypal.svg',
        status: 'activated',
      });
    }

    return methods;
  }

  /**
   * Generates the payment URL for a specific method
   * @param {string} methodId - The ID of the selected payment method
   * @returns {string}
   */
  public generatePaymentUrl(methodId: string): string {
    if (methodId === 'paypal-internal' && paypalUrl) {
      return `${paypalUrl}/order/${this._quoteId}/pay`;
    }

    if (mollieUrl) {
      return `${mollieUrl}/order/${this._quoteId}/pay?method=${methodId}`;
    }

    return '#';
  }
}
